import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Store, Layers, ExternalLink, ArrowLeft, ArrowRight, Tag, X, Heart } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import api, { apiError } from "../lib/apiClient";
import { AFFILIATE_DISCLOSURE } from "../lib/siteConfig";
import { categoryImage } from "../lib/categoryImages";
import { useDealLink } from "../context/DealLinkContext";
import { useAuth } from "../context/AuthContext";
import { useLovedDeals } from "../context/LovedDealsContext";
import ShareButton from "../components/ShareButton";
import { ImageStage } from "../components/ImageStage";
import { countsLine } from "../components/DealCard";
import { useDialogA11y } from "../lib/useDialogA11y";
export default function DealPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { openDeal } = useDealLink();
  const { user } = useAuth();
  const { lovedIds, loading: lovedLoading, toggle: toggleLoved } = useLovedDeals();
  const [deal, setDeal] = useState(undefined);
  const [childSizes, setChildSizes] = useState(null);
  const [linkModal, setLinkModal] = useState(null);
  const dialogRef = useDialogA11y(!!linkModal, () => setLinkModal(null));

  const openChildSizes = async () => {
    if (!user) { navigate("/auth"); return; }
    try {
      const { data } = await api.post(`/deals/${id}/links`, {});
      if (!data.complete) { toast.info(data.message || "This set is no longer complete."); return; }
      setLinkModal({ links: data.links });
    } catch (err) {
      if (err.response?.status === 403) { navigate("/upgrade"); return; }
      toast.error(apiError(err.response?.data?.detail) || "Could not open this set.");
    }
  };

  const buyItem = (link) => {
    api.post("/clicks", { product_id: link.product_id, collection_id: id, retailer: link.retailer }).catch(() => {});
  };

  const validUrl = (u) => typeof u === "string" && /^https?:\/\//i.test(u.trim());
  useEffect(() => {
    setDeal(undefined);
    api.get(`/deals/item/${id}`).then(({ data }) => setDeal(data)).catch(() => setDeal(null));
    // Record an internal SIBLINGO detail view (privacy-safe; distinct from a retailer click).
    api.post(`/deals/${id}/view`, { member: !!user }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Size decisions stay with the parent. This page never requests predicted sizes.

  if (deal === undefined) {
    return <div className="max-w-3xl mx-auto py-16"><div className="bg-white rounded-3xl border border-line h-96 animate-pulse" /></div>;
  }
  if (deal === null) {
    return (
      <div className="max-w-lg mx-auto text-center py-20 fade-up" data-testid="deal-not-found">
        <div className="w-16 h-16 rounded-3xl bg-blush text-brand flex items-center justify-center mx-auto mb-5"><Tag size={30} /></div>
        <h1 className="font-heading font-black text-2xl text-ink">Set not found</h1>
        <p className="text-ink-soft mt-2">This collection may no longer be available.</p>
        <button onClick={() => navigate("/family-deals")} className="mt-6 h-12 px-6 rounded-full bg-brand text-white font-bold hover:bg-brand-hover transition-all">Browse Curated Family Sets</button>
      </div>
    );
  }

  if (deal.unavailable) {
    return (
      <div className="max-w-lg mx-auto text-center py-20 fade-up" data-testid="deal-unavailable">
        <div className="w-16 h-16 rounded-3xl bg-blush text-brand flex items-center justify-center mx-auto mb-5"><Tag size={30} /></div>
        <p className="text-xs font-bold uppercase tracking-wide text-ink-muted">Saved collection</p>
        <h1 className="font-heading font-black text-2xl text-ink mt-1">{deal.name}</h1>
        <p className="text-ink-soft mt-2" data-testid="deal-unavailable-message">{deal.message}</p>
        <button onClick={() => navigate("/family-deals")} className="mt-6 h-12 px-6 rounded-full bg-brand text-white font-bold hover:bg-brand-hover transition-all" data-testid="deal-unavailable-back">See what's coming</button>
      </div>
    );
  }
  const isColl = deal.kind === "collection";
  const items = isColl ? (deal.items || []) : [];
  const title = isColl ? deal.name : deal.title;
  const matchLabel = deal.complete_the_look
    ? "Complete the Look"
    : (deal.match_type
      ? (String(deal.match_type) === "exact_match" ? "Exact Match" : "Coordinating")
      : "");
  const coordLabel = deal.coordination_method === "same_color" ? "Same colour family" : "";
  const retailerLabel = deal.store || deal.partner_name || "the retailer";
  const buyUrl = isColl ? null : (deal.kind === "affiliate" ? (deal.affiliate_url || deal.original_url) : deal.url);
  const canBuy = validUrl(buyUrl) && !/\/(search|s)\b|searchText=|searchTerm=|[?&](k|q|searchText|searchTerm)=/i.test(buyUrl || "");
  const image = isColl ? (deal.image || (items[0] && items[0].image)) : (deal.image || categoryImage(deal));
  const price = isColl
    ? (deal.family_total != null ? deal.family_total : items.reduce((s, i) => s + (i.price || 0), 0))
    : deal.price;

  const SITE_ABS = "https://siblingo.co";
  const absUrl = (u) => (u ? (String(u).startsWith("http") ? u : SITE_ABS + u) : undefined);
  const breadcrumb = {
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ABS}/` },
      { "@type": "ListItem", position: 2, name: "Curated Family Sets", item: `${SITE_ABS}/family-deals` },
      ...(isColl && deal.occasion ? [{ "@type": "ListItem", position: 3, name: ({ pajamas: "Pajamas", weekday: "Weekday", school: "School", shabbos: "Shabbos", wedding_formal: "Wedding / Formal", yom_tov: "Yom Tov", family_photos: "Family Photos" }[deal.occasion] || String(deal.occasion).replace(/_/g, " ")), item: `${SITE_ABS}/family-deals?occasion=${deal.occasion}` }] : []),
      { "@type": "ListItem", position: isColl && deal.occasion ? 4 : 3, name: title, item: `${SITE_ABS}/deal/${id}` },
    ],
  };
  const jsonLd = isColl && items.length
    ? {
        "@context": "https://schema.org",
        dateModified: deal.last_verified || undefined,
        "@graph": [
          breadcrumb,
          {
            // One ListItem per VISIBLE placement (stable placement identity); two children reusing one retailer
            // variant share the same Product @id, so numberOfItems == visible pieces while the Product stays truthful.
            "@type": "ItemList", name: title, url: `${SITE_ABS}/deal/${id}`, numberOfItems: items.length,
            itemListElement: items.map((it, i) => {
              const variantKey = it.retailer_variant_id || it.id;
              const productId = `${SITE_ABS}/deal/${id}#product-${variantKey}`;
              return {
                "@type": "ListItem", position: i + 1, "@id": `${SITE_ABS}/deal/${id}#placement-${it.id}`,
                name: `${it.retailer_title || it.name || it.title}${it.family_member ? ` — for ${it.family_member}` : ""}${it.size ? ` · ${Array.isArray(it.size) ? it.size[0] : it.size}` : ""}`,
                item: {
                  "@type": "Product", "@id": productId, name: it.retailer_title || it.name || it.title, sku: it.retailer_sku || undefined,
                  productID: it.retailer_product_id ? `${it.retailer || "retailer"}:${it.retailer_product_id}${it.retailer_variant_id ? ":" + it.retailer_variant_id : ""}` : undefined,
                  color: it.color || undefined, size: it.size ? (Array.isArray(it.size) ? it.size[0] : it.size) : undefined,
                  audience: it.family_member ? { "@type": "PeopleAudience", suggestedGender: it.gender === "boy" ? "male" : it.gender === "girl" ? "female" : "unisex", name: it.family_member } : undefined,
                  brand: { "@type": "Brand", name: it.brand || it.retailer || "Leveret" },
                  image: absUrl(it.image), url: absUrl(it.canonical_url || it.source_url),
                  offers: {
                    "@type": "Offer", price: it.price, priceCurrency: it.currency || "USD",
                    availability: (it.availability || "in_stock") === "in_stock" ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                    url: absUrl(it.canonical_url || it.source_url), seller: { "@type": "Organization", name: it.retailer || it.seller || "Retailer" },
                  },
                },
              };
            }),
          },
        ],
      }
    : { "@context": "https://schema.org", "@graph": [breadcrumb] };
  const extrasCount = deal.extras_count || 0;
  const priceLabel = (deal.complete_the_look || extrasCount > 0) ? "Collection total" : "Complete outfit total";
  const verifiedDate = deal.last_verified ? new Date(deal.last_verified).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";

  const openPurchase = () => {
    if (isColl) { navigate("/family-deals"); return; }
    if (deal.kind === "affiliate") {
      openDeal({ id: deal.id, checkUrl: deal.affiliate_url || deal.original_url, openUrl: `/go/${deal.id}`, department: deal.department });
    } else {
      openDeal({ id: deal.id, checkUrl: deal.url, url: deal.url, department: deal.department });
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5 fade-up" data-testid={`deal-page-${deal.id}`}>
      <Seo title={title} description={isColl ? `${title} — a curated ${matchLabel || "family"} set on SIBLINGO.` : title} path={`/deal/${id}`} image={image} jsonLd={jsonLd} />
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-ink-soft hover:text-brand font-semibold text-sm"><ArrowLeft size={16} /> Back</button>

      <div className="bg-white rounded-3xl border border-line shadow-card overflow-hidden">
        <ImageStage src={image} alt={`${title} — product image`} ratio="landscape" pad="p-4" testid="deal-image-stage" className="max-h-[520px]">
          {deal.category && (
            <span className="absolute top-4 right-4 bg-white/90 backdrop-blur text-ink text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1"><Layers size={12} /> {deal.category}</span>
          )}
        </ImageStage>
        <div className="p-6 sm:p-8">
          <p className="text-xs font-bold uppercase tracking-wider text-brand flex items-center gap-1"><Store size={13} /> {deal.store || deal.partner_name || "SIBLINGO"}</p>
          <h1 className="font-heading font-black text-2xl sm:text-3xl text-ink leading-snug mt-1">{title}</h1>
          {isColl && matchLabel && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="deal-match-label">
              <span className="text-xs font-bold bg-brand/10 text-brand border border-brand/20 rounded-full px-2.5 py-1">{matchLabel}</span>
              {coordLabel && <span className="text-xs font-semibold bg-cream text-ink-soft border border-line rounded-full px-2.5 py-1 capitalize">{coordLabel}</span>}
            </div>
          )}
          {price != null && price > 0 && (
            <div className="mt-3">
              {isColl && <p className="text-xs font-bold uppercase tracking-wider text-ink-muted" data-testid="deal-total-label">{priceLabel}</p>}
              <div className="flex items-baseline gap-2">
                <span className="font-heading font-black text-3xl text-ink" data-testid="deal-total-price">${Number(price).toFixed(2)}</span>
                <span className="text-xs font-bold text-ink-muted" data-testid="deal-currency">{deal.currency || (items[0] && items[0].currency) || "USD"}</span>
                {!isColl && deal.was > 0 && <span className="text-ink-muted line-through">${deal.was.toFixed(2)}</span>}
              </div>
              {isColl && extrasCount > 0 && (
                <p className="text-xs text-ink-soft mt-1 font-semibold" data-testid="deal-subtotals">Main outfits ${Number(deal.main_outfit_total || 0).toFixed(2)} · Optional extras ${Number(deal.extras_total || 0).toFixed(2)}</p>
              )}
              {isColl && <p className="text-xs text-ink-muted mt-1" data-testid="deal-total-disclaimer">Total recomputed from today's member prices at the retailer; prices and stock may change — confirm on the retailer's site before buying.</p>}
              {isColl && <p className="text-xs text-ink-muted mt-0.5" data-testid="deal-counts">{countsLine(deal)}</p>}
            </div>
          )}
          {deal.sizes && (deal.sizes.length > 0) && <p className="text-sm text-ink-soft mt-2">Sizes: {Array.isArray(deal.sizes) ? deal.sizes.join(", ") : deal.sizes}</p>}
          {Array.isArray(deal.colors) && deal.colors.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="deal-colors">
              <span className="text-sm text-ink-soft">Colors:</span>
              {deal.colors.map((c) => (
                <span key={c} className="text-xs font-semibold bg-cream border border-line rounded-full px-2.5 py-0.5 text-ink-soft">{c}</span>
              ))}
            </div>
          )}
          {deal.style && <p className="text-sm text-ink-soft mt-2 capitalize">Style: {deal.style}</p>}
          {(deal.size_chart_text || (Array.isArray(deal.size_chart) && deal.size_chart.length > 0)) && (
            <details className="mt-3 rounded-2xl border border-line bg-cream/60 p-3" data-testid="deal-size-chart">
              <summary className="text-sm font-bold text-ink cursor-pointer select-none">Size chart</summary>
              {deal.size_chart_text
                ? <pre className="mt-2 text-xs text-ink-soft whitespace-pre-wrap font-body leading-relaxed">{deal.size_chart_text}</pre>
                : (
                  <div className="mt-2 space-y-1">
                    {deal.size_chart.map((row, i) => {
                      const text = typeof row === "object" && row !== null
                        ? Object.entries(row).map(([k, v]) => `${k}: ${v}`).join("  ·  ")
                        : String(row);
                      return (
                        <p key={`${text}-${i}`} className="text-xs text-ink-soft">{text}</p>
                      );
                    })}
                  </div>
                )}
            </details>
          )}
          {deal.valid_to && <p className="text-sm font-bold text-gold-text mt-2">Valid until {deal.valid_to}</p>}
          {deal.confirmed_on && <p className="text-xs text-ink-muted mt-3" data-testid="deal-confirmed-on">Last confirmed on {deal.confirmed_on}</p>}
          {!isColl && (
            <p className="text-xs text-ink-muted mt-1" data-testid="deal-price-warning">Prices and stock are set by {retailerLabel} and may change — always confirm on their site before buying.</p>
          )}
          {isColl && (
            <div className="mt-3 space-y-0.5">
              {deal.last_verified && (
                <p className="text-xs text-ink-muted" data-testid="deal-last-verified">
                  Prices and stock last verified {verifiedDate}.
                </p>
              )}
              <p className="text-xs text-ink-muted" data-testid="deal-retailer-disclaimer">
                Prices, stock and sizes are set by {deal.main_source || "the retailer"} and can change at any time — always confirm on their site before buying.
              </p>
            </div>
          )}
          {isColl && items.length > 0 && (
            <div className="mt-6" data-testid="collection-pieces">
              <p className="text-xs font-bold uppercase tracking-wider text-ink-muted mb-3">In this collection · {countsLine(deal)}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {items.map((it) => (
                  <div key={it.id} data-testid={`collection-piece-${it.id}`} className="rounded-2xl border border-line overflow-hidden bg-white">
                    <ImageStage src={it.image} alt={`${it.name || it.title}${it.color ? ` — ${it.color}` : ""}`} ratio="portrait" pad="p-2" testid={`piece-image-${it.id}`} />
                    <p className="p-2.5 pb-1 text-xs font-semibold text-ink leading-snug line-clamp-2" data-testid={`piece-title-${it.id}`}>{it.retailer_title || it.name || it.title}</p>
                    {(it.family_member || it.size || it.role) && (
                      <p className="px-2.5 pb-1 text-[10px] font-bold text-brand leading-tight" data-testid={`piece-member-${it.id}`}>
                        {it.family_member ? `For ${it.family_member}` : ""}{it.role && it.role.includes("finishing") ? " · Optional extra" : " · Main outfit"}
                      </p>
                    )}
                    <dl className="px-2.5 pb-1 text-[10px] text-ink-soft leading-tight grid grid-cols-[auto_1fr] gap-x-1.5 gap-y-0.5" data-testid={`piece-details-${it.id}`}>
                      {it.color && <><dt className="text-ink-muted">Color</dt><dd className="font-semibold text-ink" data-testid={`piece-color-${it.id}`}>{it.color}</dd></>}
                      {it.size && <><dt className="text-ink-muted">Size</dt><dd className="font-semibold text-ink" data-testid={`piece-size-${it.id}`}>{Array.isArray(it.size) ? it.size[0] : it.size}</dd></>}
                      {it.price != null && <><dt className="text-ink-muted">Price</dt><dd className="font-semibold text-ink" data-testid={`piece-price-${it.id}`}>${Number(it.price).toFixed(2)} {it.currency || "USD"}</dd></>}
                      <dt className="text-ink-muted">Stock</dt><dd className={`font-semibold ${!it.verified_at ? "text-ink-muted" : (it.availability || "in_stock") === "in_stock" ? "text-success" : "text-brand"}`} data-testid={`piece-stock-${it.id}`}>{!it.verified_at ? "Not re-verified — check at retailer" : (it.availability || "in_stock") === "in_stock" ? "In stock at last check" : "Sold out at last check"}</dd>
                      <dt className="text-ink-muted">Retailer</dt><dd className="font-semibold text-ink" data-testid={`piece-retailer-${it.id}`}>{it.retailer || it.brand}</dd>
                      <dt className="text-ink-muted">Verified</dt><dd data-testid={`piece-verified-${it.id}`}>{it.verified_at ? new Date(it.verified_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Pending re-check"}</dd>
                    </dl>
                    {(() => {
                      // Approved affiliate tracked URL first (only then rel="sponsored"); otherwise the canonical product URL.
                      // Shopify variant deep link (?variant=<id>) is officially supported by the retailer; never a guessed tag.
                      // Buy is hidden when the retailer destination failed verification (homepage/search/broken redirect).
                      const base = it.canonical_url || it.source_url || it.url || "";
                      const variantUrl = it.retailer_variant_id && /^https?:\/\//.test(base) ? `${base}${base.includes("?") ? "&" : "?"}variant=${encodeURIComponent(it.retailer_variant_id)}` : base;
                      const purl = it.affiliate_url || variantUrl;
                      const sponsored = !!it.affiliate_url;
                      if (it.destination_ok === false) {
                        return <p className="mx-2.5 my-1 text-[10px] text-ink-muted" data-testid={`piece-buy-unavailable-${it.id}`}>Retailer link not verified — Buy hidden.</p>;
                      }
                      return /^https?:\/\//.test(purl) ? (
                        <>
                        <a
                          href={purl}
                          target="_blank"
                          rel={sponsored ? "noopener noreferrer nofollow sponsored" : "noopener noreferrer nofollow"}
                          data-testid={`piece-buy-${it.id}`}
                          data-variant-link={it.retailer_variant_id ? "true" : "false"}
                          aria-label={`Buy ${it.retailer_title || it.name || it.title}${it.family_member ? ` for ${it.family_member}` : ""}${it.color ? ` · ${it.color}` : ""}${it.size ? ` · ${Array.isArray(it.size) ? it.size[0] : it.size}` : ""} at ${it.retailer || it.brand || "the retailer"} (opens in a new tab)`}
                          className="mx-2.5 my-1 flex items-center justify-center gap-1 h-11 rounded-full bg-brand text-white text-[11px] font-bold hover:bg-brand-hover transition-all no-underline"
                        >
                          Buy at {it.retailer || it.brand || "retailer"} <ExternalLink size={11} />
                        </a>
                        {sponsored && <p className="mx-2.5 text-[10px] font-bold text-gold-text" data-testid={`piece-affiliate-label-${it.id}`}>Affiliate link</p>}
                        {(it.color || it.size) && (
                          <p className="mx-2.5 mb-1 text-[10px] text-ink-muted leading-tight" data-testid={`piece-variant-note-${it.id}`}>
                            At {it.retailer || "the retailer"}, choose {[it.color, Array.isArray(it.size) ? it.size[0] : it.size].filter(Boolean).join(" · ")}.{it.retailer_variant_id ? " The link opens that variant." : ""} Nothing is added to a cart.
                          </p>
                        )}
                        </>
                      ) : null;
                    })()}
                    {childSizes && childSizes.map[it.id] && childSizes.map[it.id].length > 0 ? (
                      <div className="px-2.5 pb-2.5 space-y-1" data-testid={`piece-sizes-${it.id}`}>
                        {childSizes.map[it.id].map((r) => (
                          <div key={r.child_id} title={r.explanation} data-testid={`piece-size-${it.id}-${r.child_id}`}
                            className="rounded-lg bg-cream/70 border border-line px-2 py-1">
                            <p className="text-[11px] font-bold text-ink leading-tight truncate">{r.nickname}</p>
                            {r.available ? (
                              <p className="text-[11px] leading-tight" data-testid={`piece-size-state-${it.id}-${r.child_id}`}>
                                <span className="font-extrabold text-brand">{r.brand} {r.size}</span>
                                {r.alternate && <span className="text-ink-muted"> / {r.alternate}</span>}
                                <span className="text-ink-muted"> · {r.clothing_type}/{r.fit}</span>
                                <span className={`ml-1 font-bold ${r.confidence === "High" ? "text-success" : r.confidence === "Medium" ? "text-gold-text" : "text-ink-muted"}`}>{r.confidence}</span>
                                {r.state === "between_sizes" && r.message && <span className="block text-ink-soft mt-0.5">{r.message}</span>}
                                {r.detail && <span className="block text-ink-muted mt-0.5">{r.detail}</span>}
                              </p>
                            ) : (
                              <p className="text-[11px] text-ink-soft leading-tight" data-testid={`piece-size-state-${it.id}-${r.child_id}`}>
                                {r.message || "Choose the final size using the retailer's official chart."}
                                {r.state === "chart_unverified" && r.detail && <span className="block text-ink-muted mt-0.5" data-testid={`piece-size-chart-detail-${it.id}-${r.child_id}`}>{r.detail}</span>}
                                {r.cta && <Link to={r.cta} className="ml-1 font-bold text-brand underline">Update profile</Link>}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (childSizes && childSizes.count > 0 && (
                      <p className="px-2.5 pb-2.5 text-[11px] text-ink-muted leading-tight" data-testid={`piece-noeligible-${it.id}`}>No eligible child for this item.</p>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 mt-6">
            {isColl ? (
              <button
                onClick={openChildSizes}
                data-testid="deal-page-open-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 h-13 py-3.5 rounded-full bg-brand text-white font-bold hover:bg-brand-hover active:scale-95 transition-all shadow-md"
              >
                {user ? "Review retailer links" : "Sign in to save this set"} <ArrowRight size={18} />
              </button>
            ) : canBuy ? (
              <button
                onClick={openPurchase}
                data-testid="deal-page-open-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 h-13 py-3.5 rounded-full bg-brand text-white font-bold hover:bg-brand-hover active:scale-95 transition-all shadow-md"
              >
                Buy at {retailerLabel} <ExternalLink size={18} />
              </button>
            ) : (
              <span
                data-testid="deal-page-link-unavailable"
                className="flex-1 inline-flex items-center justify-center gap-2 h-13 py-3.5 rounded-full bg-cream text-ink-muted border border-line font-bold"
              >
                Direct link not verified yet
              </span>
            )}
            {isColl && (
              <button
                type="button"
                onClick={() => toggleLoved(deal)}
                disabled={lovedLoading}
                aria-pressed={lovedIds.has(deal.id)}
                aria-label={lovedIds.has(deal.id) ? "Remove from saved collections" : "Save this collection"}
                data-testid="deal-page-love"
                className={`h-12 min-w-12 px-4 rounded-full border-2 font-bold inline-flex items-center justify-center gap-2 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:opacity-60 ${lovedIds.has(deal.id) ? "bg-brand border-brand text-white" : "bg-white border-brand/30 text-brand hover:bg-blush"}`}
              >
                <Heart size={19} fill={lovedIds.has(deal.id) ? "currentColor" : "none"} aria-hidden="true" />
                <span className="hidden sm:inline">{lovedIds.has(deal.id) ? "Loved" : "Love this deal"}</span>
              </button>
            )}
            <ShareButton dealId={deal.id} title={title} store={deal.store} price={price} was={deal.was} />
          </div>
          {isColl && !user && (
            <p className="text-xs text-ink-muted mt-3">Choose sizes using each retailer's official chart. Sign in to save this collection.</p>
          )}
          <p className="text-[11px] text-ink-muted mt-3 leading-snug">{AFFILIATE_DISCLOSURE}</p>
        </div>
      </div>

      {linkModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto bg-ink/50 backdrop-blur-sm" onClick={() => setLinkModal(null)}>
          <div className="flex min-h-full items-start sm:items-center justify-center p-4">
          <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="child-sizes-title" tabIndex={-1} className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-lift my-8 outline-none" onClick={(e) => e.stopPropagation()} data-testid="child-sizes-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 id="child-sizes-title" className="font-heading font-extrabold text-xl text-ink">Review this set</h3>
              <button onClick={() => setLinkModal(null)} data-testid="close-child-sizes-modal" aria-label="Close" className="p-2 rounded-full hover:bg-cream min-w-[44px] min-h-[44px] flex items-center justify-center"><X size={20} /></button>
            </div>
            <p className="text-sm text-ink-soft mb-4">SIBLINGO doesn't process purchases — you buy directly at each store. Sizes below come only from verified retailer charts.</p>
            <div className="space-y-3 max-h-96 overflow-auto">
              {linkModal.links.map((l) => {
                const recs = (childSizes && childSizes.map[l.product_id]) || [];
                return (
                  <div key={l.product_id} data-testid={`modal-product-${l.product_id}`} className="rounded-2xl border border-line p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-bold text-ink text-sm">{l.name}</p>
                        <p className="text-xs text-ink-muted">{l.retailer || "Retailer"}{l.price ? ` · $${l.price}` : ""}</p>
                      </div>
                      {(() => {
                        const buyUrl = validUrl(l.affiliate_url) ? l.affiliate_url : (validUrl(l.url) ? l.url : null);
                        return buyUrl ? (
                          <a
                            href={buyUrl}
                            target="_blank"
                            rel={validUrl(l.affiliate_url) ? "noopener noreferrer nofollow sponsored" : "noopener noreferrer nofollow"}
                            onClick={() => buyItem(l)}
                            data-testid={`modal-buy-${l.product_id}`}
                            className="shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-brand text-white font-bold text-sm hover:bg-brand-hover transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
                          >
                            Buy <ExternalLink size={15} />
                          </a>
                        ) : (
                          <span data-testid={`modal-buy-unavailable-${l.product_id}`} className="shrink-0 text-xs font-semibold text-ink-muted bg-cream border border-line rounded-full px-3 py-2">
                            Link not verified yet
                          </span>
                        );
                      })()}
                    </div>
                    {recs.length > 0 ? (
                      <div className="mt-3 space-y-1.5" data-testid={`modal-sizes-${l.product_id}`}>
                        {recs.map((r) => (
                          <div key={r.child_id} className="flex items-center justify-between text-xs bg-cream/70 border border-line rounded-lg px-2.5 py-1.5">
                            <span className="font-bold text-ink capitalize">{r.nickname}</span>
                            {r.available ? (
                              <span>
                                <span className="font-extrabold text-brand">{r.brand} {r.size}</span>
                                <span className="text-ink-muted"> · {r.clothing_type}/{r.fit}</span>
                                <span className={`ml-1 font-bold ${r.confidence === "High" ? "text-success" : r.confidence === "Medium" ? "text-gold-text" : "text-ink-muted"}`}>{r.confidence}</span>
                              </span>
                            ) : (
                              <span className="text-ink-soft text-right" data-testid={`modal-size-state-${l.product_id}-${r.child_id}`}>
                                {r.message || "Choose the final size using the retailer's official chart."}
                                {r.cta && <Link to={r.cta} className="ml-1 font-bold text-brand underline">Update profile</Link>}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-2 text-xs text-ink-muted">No eligible child for this item.</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
